/**
 * Shared types for encoder poller worker thread
 */

import type { EncoderSampleData, EncoderAlertPayload } from './triage';

// ============================================================================
// Worker Message Types (Worker -> Main Thread)
// ============================================================================

/** Messages sent from worker to main thread */
export type WorkerToMainMessage =
  | { type: 'ready' }
  | { type: 'poll_complete'; timestamp: number; stats: PollCycleStats }
  | { type: 'encoder_update'; encoderId: string; sample: EncoderSampleData }
  | { type: 'encoder_alert'; alert: EncoderAlertPayload }
  | { type: 'encoder_alert_update'; encoderId: string; alertType: string; lastAnomalyAt: number; currentValue?: number }
  | { type: 'encoder_alert_resolved'; encoderId: string; alertType: string }
  | { type: 'encoder_offline'; encoderId: string }
  | { type: 'encoder_online'; encoderId: string; sample: EncoderSampleData }
  | { type: 'error'; message: string; encoderId?: string }
  | { type: 'incompatible_schema'; encoderId: string; rawData: unknown };

// ============================================================================
// Configuration
// ============================================================================

export const POLLER_CONFIG = {
  /** Encoders that haven't communicated in this many days are considered stale */
  STALE_ENCODER_DAYS: 7,
} as const;

export interface PollerConfig {
  /** Poll interval in milliseconds (default: 60000) */
  pollIntervalMs: number;
  /** Number of samples to keep in ring buffer per encoder */
  ringBufferSize: number;
}

// ============================================================================
// Stats
// ============================================================================

export interface PollerStats {
  running: boolean;
  encoderCount: number;
  triageCount: number;
  lastPollAt: number | null;
  totalPolls: number;
  ringBufferStats: RingBufferStats | null;
}

export interface PollCycleStats {
  onlineCount: number;
  offlineCount: number;
  incompatibleCount: number;
  durationMs: number;
  newlyOnline: string[];
  newlyOffline: string[];
  ringBufferStats: RingBufferStats;
}

export interface RingBufferStats {
  /** Total number of encoders being tracked in ring buffers */
  totalEncoders: number;
  /** Number of encoders in triage mode (expanded buffers) */
  triageEncoders: number;
  /** Total samples stored across all buffers */
  totalSamples: number;
  /** Estimated memory usage in bytes */
  estimatedMemoryBytes: number;
}

// ============================================================================
// Ring Buffer Entry
// ============================================================================

export interface RingBufferEntry {
  timestamp: number;
  sample: EncoderSampleData;
}
